import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, DollarSign, Percent, ChevronDown, X } from 'lucide-react';

interface RefundMenuProps {
  onRefundAll: () => void;
  onRefundCapped: (maxAmount: number) => void;
  onRefundPartial: (percentage: number) => void;
  onRefundZero?: () => void;
}

export const RefundMenu: React.FC<RefundMenuProps> = ({
  onRefundAll,
  onRefundCapped,
  onRefundPartial,
  onRefundZero,
}) => {
  const [showCappedDialog, setShowCappedDialog] = useState(false);
  const [showPartialDialog, setShowPartialDialog] = useState(false);
  const [cappedAmount, setCappedAmount] = useState('');
  const [partialPercentage, setPartialPercentage] = useState('');

  const handleRefundAll = () => {
    onRefundAll();
  };

  const handleRefundZero = () => {
    if (onRefundZero) {
      onRefundZero();
    }
  };

  const handleCappedSubmit = () => {
    const amount = parseFloat(cappedAmount);
    if (!isNaN(amount) && amount > 0) {
      onRefundCapped(amount);
      setShowCappedDialog(false);
      setCappedAmount('');
    }
  };

  const handlePartialSubmit = () => {
    const percentage = parseFloat(partialPercentage);
    if (!isNaN(percentage) && percentage > 0 && percentage <= 100) {
      onRefundPartial(percentage);
      setShowPartialDialog(false);
      setPartialPercentage('');
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="gap-2">
            Refund
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={handleRefundAll}>
            <Check className="mr-2 h-4 w-4" />
            Refund All
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowCappedDialog(true)}>
            <DollarSign className="mr-2 h-4 w-4" />
            Capped Refund
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setShowPartialDialog(true)}>
            <Percent className="mr-2 h-4 w-4" />
            Partial Refund
          </DropdownMenuItem>
          {onRefundZero && (
            <DropdownMenuItem onClick={handleRefundZero}>
              <X className="mr-2 h-4 w-4" />
              0 Refund All
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Capped Refund Dialog */}
      <Dialog open={showCappedDialog} onOpenChange={setShowCappedDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Capped Refund</DialogTitle>
            <DialogDescription>
              Enter the maximum refund amount. Items will be refunded up to this cap.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="capped-amount">Maximum Amount</Label>
              <Input
                id="capped-amount"
                type="number"
                placeholder="Enter max amount..."
                value={cappedAmount}
                onChange={(e) => setCappedAmount(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleCappedSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCappedDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleCappedSubmit}>Apply Capped Refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Refund Dialog */}
      <Dialog open={showPartialDialog} onOpenChange={setShowPartialDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Partial Refund</DialogTitle>
            <DialogDescription>
              Enter the percentage to refund for all items in this section.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="partial-percentage">Percentage (%)</Label>
              <Input
                id="partial-percentage"
                type="number"
                min="0"
                max="100"
                placeholder="Enter percentage (0-100)..."
                value={partialPercentage}
                onChange={(e) => setPartialPercentage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePartialSubmit();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartialDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handlePartialSubmit}>Apply Partial Refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
